import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { brand } from "../../../config/theme";
import Modal from "../../../components/base/Modal";
import { buildThemeClasses } from "../theme";
import {
  fetchPipelines,
  fetchCategories,
  createPipeline,
  updatePipeline,
  deletePipeline,
  createCategory,
  updateCategory,
  deleteCategory,
  fetchPipelineTemplates,
  applyPipelineTemplate,
  categoryClasses,
  CATEGORY_COLOR_TOKENS,
  TERMINAL_LABELS,
  type Pipeline,
  type PipelineCategory,
  type PipelineTemplate,
  type CategoryTerminal,
} from "../services/pipelinesService";
import { LEAD_PRODUCTS } from "../services/leadsService";

import { DIALOG_COLORS } from "../../../components/ui";
// ===========================================================================
// Configuración → Pipelines: admins create/edit the pipeline boards and the
// categories (columns) that fill them. A category can belong to many pipelines.
// The six "del sistema" categories back the lead lifecycle and can be renamed /
// recolored but not deleted; the default pipeline cannot be deleted.
// ===========================================================================

type TC = ReturnType<typeof buildThemeClasses>;

// `view` splits this into two menu entries: "Categorías" (the stages) and
// "Pipelines" (the boards built from them). Omit it to render both sections.
export default function PipelinesSettings({ view }: { view?: "categorias" | "pipelines" | "templates" } = {}) {
  const tc = buildThemeClasses();
  const showCats = !view || view === "categorias";
  const showPipes = !view || view === "pipelines";
  const showTemplates = view === "templates";

  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [categories, setCategories] = useState<PipelineCategory[]>([]);
  const [templates, setTemplates] = useState<PipelineTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  const [editCat, setEditCat] = useState<PipelineCategory | "new" | null>(null);
  const [editPipe, setEditPipe] = useState<Pipeline | "new" | null>(null);

  const load = async () => {
    try {
      const [pipes, cats, tpls] = await Promise.all([
        fetchPipelines(),
        fetchCategories(),
        showTemplates ? fetchPipelineTemplates() : Promise.resolve(templates),
      ]);
      setPipelines(pipes);
      setCategories(cats);
      setTemplates(tpls);
    } catch {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo cargar la configuración de pipelines.", confirmButtonColor: brand.primary });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const catById = useMemo(
    () => categories.reduce((acc, c) => ((acc[c.id] = c), acc), {} as Record<string, PipelineCategory>),
    [categories],
  );

  const removeCategory = async (cat: PipelineCategory) => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Eliminar categoría",
      text: `¿Eliminar la categoría "${cat.name}"? Se quitará de todos los pipelines.`,
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: DIALOG_COLORS.danger,
    });
    if (!res.isConfirmed) return;
    try {
      await deleteCategory(cat.id);
      await load();
    } catch (e) {
      const err = e as { response?: { data?: { data?: { message?: string }; message?: string } } };
      Swal.fire({ icon: "error", title: "Error", text: err.response?.data?.data?.message || "No se pudo eliminar la categoría.", confirmButtonColor: brand.primary });
    }
  };

  const removePipeline = async (pipe: Pipeline) => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Eliminar pipeline",
      text: `¿Eliminar el pipeline "${pipe.name}"?`,
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: DIALOG_COLORS.danger,
    });
    if (!res.isConfirmed) return;
    try {
      await deletePipeline(pipe.id);
      await load();
    } catch {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo eliminar el pipeline.", confirmButtonColor: brand.primary });
    }
  };

  // Ask for a pipeline name + the product it tracks, used when instantiating a
  // template or duplicating an existing board.
  const askPipelineMeta = async (defaultName: string, defaultProduct: string | null) => {
    const options = ["", ...LEAD_PRODUCTS]
      .map((p) => `<option value="${p}" ${(defaultProduct ?? "") === p ? "selected" : ""}>${p || "Todos los productos"}</option>`)
      .join("");
    const res = await Swal.fire({
      title: "Crear pipeline",
      html:
        `<input id="swal-pl-name" class="swal2-input" placeholder="Nombre del pipeline" value="${defaultName.replace(/"/g, "&quot;")}">` +
        `<select id="swal-pl-product" class="swal2-input">${options}</select>`,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Crear",
      cancelButtonText: "Cancelar",
      confirmButtonColor: brand.primary,
      preConfirm: () => {
        const name = (document.getElementById("swal-pl-name") as HTMLInputElement)?.value?.trim();
        const product = (document.getElementById("swal-pl-product") as HTMLSelectElement)?.value || null;
        if (!name) {
          Swal.showValidationMessage("El nombre es obligatorio");
          return false;
        }
        return { name, product };
      },
    });
    return res.isConfirmed ? (res.value as { name: string; product: string | null }) : null;
  };

  const createFromTemplate = async (tpl: PipelineTemplate) => {
    const meta = await askPipelineMeta(tpl.name, null);
    if (!meta) return;
    try {
      const ids = await applyPipelineTemplate(tpl.key);
      await createPipeline({ name: meta.name, description: tpl.description, product: meta.product, categoryIds: ids });
      await load();
      Swal.fire({ icon: "success", title: "Pipeline creado", timer: 1400, showConfirmButton: false });
    } catch {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo crear el pipeline.", confirmButtonColor: brand.primary });
    }
  };

  const duplicatePipeline = async (p: Pipeline) => {
    const meta = await askPipelineMeta(`Copia de ${p.name}`, p.product);
    if (!meta) return;
    try {
      await createPipeline({ name: meta.name, description: p.description, product: meta.product, categoryIds: p.categoryIds || [] });
      await load();
      Swal.fire({ icon: "success", title: "Pipeline creado", timer: 1400, showConfirmButton: false });
    } catch {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo duplicar el pipeline.", confirmButtonColor: brand.primary });
    }
  };

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-5xl space-y-8">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-500/15 text-slate-500">
              <i className={`${view === "categorias" ? "ri-price-tag-3-line" : view === "templates" ? "ri-layout-grid-line" : "ri-flow-chart"} text-xl`} />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>
                {view === "categorias" ? "Categorías" : view === "pipelines" ? "Pipelines" : view === "templates" ? "Plantillas" : "Pipelines y categorías"}
              </h1>
              <p className={`text-sm ${tc.textSecondary}`}>
                {view === "categorias"
                  ? "Define las etapas (categorías) que componen tus pipelines"
                  : view === "pipelines"
                    ? "Crea tableros de pipeline a partir de tus categorías"
                    : view === "templates"
                      ? "Crea un pipeline a partir de una plantilla predefinida o de uno existente"
                      : "Crea tableros de pipeline y las etapas (categorías) que los componen"}
              </p>
            </div>
          </div>
          <button
            onClick={() => { setLoading(true); load(); }}
            title="Recargar"
            className={`rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.textSecondary} ${tc.buttonHover}`}
          >
            <i className="ri-refresh-line" />
          </button>
        </div>

        {loading ? (
          <div className={`p-10 text-center text-sm ${tc.textSecondary}`}>
            <i className="ri-loader-4-line mr-2 animate-spin" /> Cargando...
          </div>
        ) : (
          <>
            {/* Categorías */}
            {showCats && (
            <section>
              <div className="mb-3 flex items-center justify-between">
                <h2 className={`text-base font-semibold ${tc.text}`}>Categorías</h2>
                <button
                  onClick={() => setEditCat("new")}
                  className="flex items-center gap-2 rounded-lg bg-primary-500 px-3 py-2 text-sm font-medium text-on-primary hover:bg-primary-400"
                >
                  <i className="ri-add-line" /> Nueva categoría
                </button>
              </div>
              <div className={`overflow-hidden rounded-2xl border ${tc.cardBorder} ${tc.cardBg}`}>
                {categories.map((cat) => {
                  const cc = categoryClasses(cat.color);
                  return (
                    <div key={cat.id} className={`flex items-center justify-between gap-3 border-b px-4 py-3 last:border-b-0 ${tc.cardBorder}`}>
                      <div className="flex items-center gap-3">
                        <span className={`h-3 w-3 rounded-full ${cc.dot}`} />
                        <span className={`text-sm font-medium ${tc.text}`}>{cat.name}</span>
                        {cat.isSystem && (
                          <span className="rounded-full bg-gray-400/15 px-2 py-0.5 text-[10px] font-medium text-gray-500">Sistema</span>
                        )}
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${cc.tone}`}>
                          {TERMINAL_LABELS[cat.terminal]}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setEditCat(cat)} title="Editar" className="rounded-md p-1.5 text-primary-500 hover:bg-primary-500/10">
                          <i className="ri-edit-line" />
                        </button>
                        <button
                          onClick={() => removeCategory(cat)}
                          disabled={cat.isSystem}
                          title={cat.isSystem ? "Las categorías del sistema no se pueden eliminar" : "Eliminar"}
                          className={`rounded-md p-1.5 ${cat.isSystem ? "cursor-not-allowed text-gray-400/40" : "text-red-500 hover:bg-red-500/10"}`}
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
            )}

            {/* Pipelines */}
            {showPipes && (
            <section>
              <div className="mb-3 flex items-center justify-between">
                <h2 className={`text-base font-semibold ${tc.text}`}>Pipelines</h2>
                <button
                  onClick={() => setEditPipe("new")}
                  className="flex items-center gap-2 rounded-lg bg-primary-500 px-3 py-2 text-sm font-medium text-on-primary hover:bg-primary-400"
                >
                  <i className="ri-add-line" /> Nuevo pipeline
                </button>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {pipelines.map((pipe) => (
                  <div key={pipe.id} className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
                    <div className="mb-2 flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className={`text-sm font-bold ${tc.text}`}>{pipe.name}</h3>
                          {pipe.isDefault && (
                            <span className="rounded-full bg-primary-500/15 px-2 py-0.5 text-[10px] font-medium text-primary-500">Predeterminado</span>
                          )}
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-500/15 px-2 py-0.5 text-[10px] font-medium text-slate-500">
                            <i className="ri-price-tag-3-line" />{pipe.product || "Todos los productos"}
                          </span>
                        </div>
                        {pipe.description && <p className={`text-xs ${tc.textSecondary}`}>{pipe.description}</p>}
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setEditPipe(pipe)} title="Editar" className="rounded-md p-1.5 text-primary-500 hover:bg-primary-500/10">
                          <i className="ri-edit-line" />
                        </button>
                        <button
                          onClick={() => removePipeline(pipe)}
                          disabled={pipe.isDefault}
                          title={pipe.isDefault ? "El pipeline predeterminado no se puede eliminar" : "Eliminar"}
                          className={`rounded-md p-1.5 ${pipe.isDefault ? "cursor-not-allowed text-gray-400/40" : "text-red-500 hover:bg-red-500/10"}`}
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(pipe.categoryIds || []).map((cid) => {
                        const c = catById[cid];
                        if (!c) return null;
                        const cc = categoryClasses(c.color);
                        return (
                          <span key={cid} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${cc.tone}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${cc.dot}`} />{c.name}
                          </span>
                        );
                      })}
                      {(pipe.categoryIds || []).length === 0 && (
                        <span className={`text-xs ${tc.textSecondary}`}>Sin categorías asignadas</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
            )}

            {/* Plantillas */}
            {showTemplates && (
            <>
              <section>
                <h2 className={`mb-3 text-base font-semibold ${tc.text}`}>Plantillas predefinidas</h2>
                <div className="grid gap-3 sm:grid-cols-2">
                  {templates.map((tpl) => (
                    <div key={tpl.key} className={`flex flex-col rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
                      <h3 className={`text-sm font-bold ${tc.text}`}>{tpl.name}</h3>
                      {tpl.description && <p className={`mb-2 text-xs ${tc.textSecondary}`}>{tpl.description}</p>}
                      <div className="mb-3 flex flex-1 flex-wrap gap-1.5">
                        {tpl.stages.map((s, i) => {
                          const cc = categoryClasses(s.color);
                          return (
                            <span key={`${tpl.key}-${i}`} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${cc.tone}`}>
                              <span className={`h-1.5 w-1.5 rounded-full ${cc.dot}`} />{s.name}
                            </span>
                          );
                        })}
                      </div>
                      <button
                        onClick={() => createFromTemplate(tpl)}
                        className="flex items-center justify-center gap-2 rounded-lg bg-primary-500 px-3 py-2 text-sm font-medium text-on-primary hover:bg-primary-400"
                      >
                        <i className="ri-add-line" /> Crear pipeline
                      </button>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className={`mb-1 text-base font-semibold ${tc.text}`}>A partir de un pipeline existente</h2>
                <p className={`mb-3 text-sm ${tc.textSecondary}`}>Duplica un tablero ya creado (como el predeterminado) reutilizando sus mismas categorías.</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {pipelines.map((pipe) => (
                    <div key={pipe.id} className={`flex flex-col rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
                      <div className="flex items-center gap-2">
                        <h3 className={`text-sm font-bold ${tc.text}`}>{pipe.name}</h3>
                        {pipe.isDefault && (
                          <span className="rounded-full bg-primary-500/15 px-2 py-0.5 text-[10px] font-medium text-primary-500">Predeterminado</span>
                        )}
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-500/15 px-2 py-0.5 text-[10px] font-medium text-slate-500">
                          <i className="ri-price-tag-3-line" />{pipe.product || "Todos"}
                        </span>
                      </div>
                      <div className="mb-3 mt-2 flex flex-1 flex-wrap gap-1.5">
                        {(pipe.categoryIds || []).map((cid) => {
                          const c = catById[cid];
                          if (!c) return null;
                          const cc = categoryClasses(c.color);
                          return (
                            <span key={cid} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${cc.tone}`}>
                              <span className={`h-1.5 w-1.5 rounded-full ${cc.dot}`} />{c.name}
                            </span>
                          );
                        })}
                      </div>
                      <button
                        onClick={() => duplicatePipeline(pipe)}
                        className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${tc.border} ${tc.text} ${tc.buttonHover}`}
                      >
                        <i className="ri-file-copy-line" /> Duplicar
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            </>
            )}
          </>
        )}
      </div>

      {/* Category editor */}
      {editCat && (
        <Modal
          title={editCat === "new" ? "Nueva categoría" : "Editar categoría"}
          onClose={() => setEditCat(null)}
          themeClasses={tc}
          content={
            <CategoryForm
              tc={tc}
              initial={editCat === "new" ? null : editCat}
              onCancel={() => setEditCat(null)}
              onSaved={async () => { setEditCat(null); await load(); }}
            />
          }
        />
      )}

      {/* Pipeline editor */}
      {editPipe && (
        <Modal
          title={editPipe === "new" ? "Nuevo pipeline" : "Editar pipeline"}
          onClose={() => setEditPipe(null)}
          themeClasses={tc}
          content={
            <PipelineForm
              tc={tc}
              initial={editPipe === "new" ? null : editPipe}
              categories={categories}
              pipelines={pipelines}
              onCancel={() => setEditPipe(null)}
              onSaved={async () => { setEditPipe(null); await load(); }}
            />
          }
        />
      )}
    </div>
  );
}

// --- Category form ----------------------------------------------------------

function CategoryForm({
  tc,
  initial,
  onCancel,
  onSaved,
}: {
  tc: TC;
  initial: PipelineCategory | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [color, setColor] = useState(initial?.color ?? "sky");
  const [terminal, setTerminal] = useState<CategoryTerminal>(initial?.terminal ?? "open");
  const [saving, setSaving] = useState(false);
  const isSystem = Boolean(initial?.isSystem);

  const inputClass = `w-full rounded-lg border px-3 py-2 text-sm outline-none border-line bg-app text-content`;
  const labelClass = `mb-1 block text-xs font-medium ${tc.textSecondary}`;

  const submit = async () => {
    if (!name.trim()) {
      Swal.fire({ icon: "warning", title: "Falta el nombre de la categoría", confirmButtonColor: brand.primary });
      return;
    }
    setSaving(true);
    try {
      if (initial) {
        await updateCategory(initial.id, { name: name.trim(), color, ...(isSystem ? {} : { terminal }) });
      } else {
        await createCategory({ name: name.trim(), color, terminal });
      }
      onSaved();
    } catch {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo guardar la categoría.", confirmButtonColor: brand.primary });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <label className={labelClass}>Nombre *</label>
        <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Propuesta enviada" />
      </div>

      <div>
        <label className={labelClass}>Color</label>
        <div className="flex flex-wrap gap-2">
          {CATEGORY_COLOR_TOKENS.map((token) => (
            <button
              key={token}
              type="button"
              onClick={() => setColor(token)}
              title={token}
              className={`h-7 w-7 rounded-full ${categoryClasses(token).swatch} ${
                color === token ? "ring-2 ring-offset-2 ring-primary-500 ring-offset-transparent" : "opacity-70 hover:opacity-100"
              }`}
            />
          ))}
        </div>
      </div>

      <div>
        <label className={labelClass}>Tipo de etapa</label>
        <select
          className={`${inputClass} ${isSystem ? "cursor-not-allowed opacity-60" : ""}`}
          value={terminal}
          disabled={isSystem}
          onChange={(e) => setTerminal(e.target.value as CategoryTerminal)}
        >
          <option value="open">Activa (en proceso)</option>
          <option value="won">Ganado (cierre)</option>
          <option value="lost">Perdido (cierre, se oculta tras 7 días)</option>
        </select>
        {isSystem && (
          <p className={`mt-1 text-[11px] ${tc.textSecondary}`}>El tipo de las categorías del sistema es fijo.</p>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className={`rounded-lg border px-4 py-2 text-sm ${tc.border} ${tc.textSecondary} ${tc.buttonHover}`}>
          Cancelar
        </button>
        <button onClick={submit} disabled={saving} className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary hover:bg-primary-400 disabled:opacity-60">
          {saving ? "Guardando..." : "Guardar"}
        </button>
      </div>
    </div>
  );
}

// --- Pipeline form ----------------------------------------------------------

type PipelineSource = "blank" | "template" | "copy";

function PipelineForm({
  tc,
  initial,
  categories,
  pipelines,
  onCancel,
  onSaved,
}: {
  tc: TC;
  initial: Pipeline | null;
  categories: PipelineCategory[];
  pipelines: Pipeline[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const isNew = !initial;
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [product, setProduct] = useState<string>(initial?.product ?? "");
  const [selected, setSelected] = useState<string[]>(initial?.categoryIds ?? []);
  const [saving, setSaving] = useState(false);

  // Local copy of the categories so applying a template (which may create new
  // reusable categories) immediately resolves the preview pills.
  const [cats, setCats] = useState<PipelineCategory[]>(categories);

  // "New pipeline" starting point: blank, from a template, or copied.
  const [source, setSource] = useState<PipelineSource>("blank");
  const [templates, setTemplates] = useState<PipelineTemplate[]>([]);
  const [templateKey, setTemplateKey] = useState("");
  const [copyId, setCopyId] = useState("");
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!isNew) return;
    fetchPipelineTemplates().then(setTemplates).catch(() => setTemplates([]));
  }, [isNew]);

  const catById = useMemo(
    () => cats.reduce((acc, c) => ((acc[c.id] = c), acc), {} as Record<string, PipelineCategory>),
    [cats],
  );
  const available = cats.filter((c) => !selected.includes(c.id));

  const chooseSource = (next: PipelineSource) => {
    setSource(next);
    setTemplateKey("");
    setCopyId("");
    setSelected([]);
  };

  const chooseTemplate = async (key: string) => {
    setTemplateKey(key);
    if (!key) { setSelected([]); return; }
    setApplying(true);
    try {
      const ids = await applyPipelineTemplate(key);
      const fresh = await fetchCategories();
      setCats(fresh);
      setSelected(ids);
      const tpl = templates.find((t) => t.key === key);
      if (tpl) {
        if (!name.trim()) setName(tpl.name);
        if (!description?.trim()) setDescription(tpl.description ?? "");
      }
    } catch {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo aplicar la plantilla.", confirmButtonColor: brand.primary });
      setTemplateKey("");
    } finally {
      setApplying(false);
    }
  };

  const chooseCopy = (id: string) => {
    setCopyId(id);
    const src = pipelines.find((p) => p.id === id);
    if (!src) { setSelected([]); return; }
    setSelected([...(src.categoryIds || [])]);
    if (!name.trim()) setName(`Copia de ${src.name}`);
    if (!description?.trim()) setDescription(src.description ?? "");
    if (!product) setProduct(src.product ?? "");
  };

  const inputClass = `w-full rounded-lg border px-3 py-2 text-sm outline-none border-line bg-app text-content`;
  const labelClass = `mb-1 block text-xs font-medium ${tc.textSecondary}`;

  const move = (idx: number, dir: -1 | 1) => {
    setSelected((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const submit = async () => {
    if (!name.trim()) {
      Swal.fire({ icon: "warning", title: "Falta el nombre del pipeline", confirmButtonColor: brand.primary });
      return;
    }
    setSaving(true);
    try {
      const body = { name: name.trim(), description: description.trim() || null, product: product || null, categoryIds: selected };
      if (initial) await updatePipeline(initial.id, body);
      else await createPipeline(body);
      onSaved();
    } catch {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo guardar el pipeline.", confirmButtonColor: brand.primary });
    } finally {
      setSaving(false);
    }
  };

  const sourceTabs: { key: PipelineSource; label: string; icon: string }[] = [
    { key: "blank", label: "En blanco", icon: "ri-file-line" },
    { key: "template", label: "Desde plantilla", icon: "ri-layout-grid-line" },
    { key: "copy", label: "Copiar de existente", icon: "ri-file-copy-line" },
  ];

  return (
    <div className="space-y-3">
      {isNew && (
        <div>
          <label className={labelClass}>Punto de partida</label>
          <div className="grid grid-cols-3 gap-1.5">
            {sourceTabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => chooseSource(t.key)}
                className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-xs font-medium transition ${
                  source === t.key
                    ? "border-primary-500 bg-primary-500/10 text-primary-500"
                    : `${tc.border} ${tc.textSecondary} ${tc.buttonHover}`
                }`}
              >
                <i className={t.icon} /> {t.label}
              </button>
            ))}
          </div>

          {source === "template" && (
            <div className="mt-2">
              <select className={inputClass} value={templateKey} onChange={(e) => chooseTemplate(e.target.value)} disabled={applying}>
                <option value="">{applying ? "Aplicando…" : "Selecciona una plantilla…"}</option>
                {templates.map((t) => (
                  <option key={t.key} value={t.key}>{t.name}</option>
                ))}
              </select>
              {templateKey && (
                <p className={`mt-1 text-[11px] ${tc.textSecondary}`}>
                  {templates.find((t) => t.key === templateKey)?.description}
                </p>
              )}
            </div>
          )}

          {source === "copy" && (
            <div className="mt-2">
              <select className={inputClass} value={copyId} onChange={(e) => chooseCopy(e.target.value)}>
                <option value="">Selecciona un pipeline…</option>
                {pipelines.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <p className={`mt-1 text-[11px] ${tc.textSecondary}`}>Se reutilizan las mismas categorías del pipeline original.</p>
            </div>
          )}
        </div>
      )}

      <div>
        <label className={labelClass}>Nombre *</label>
        <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Pipeline de ventas B2B" />
      </div>
      <div>
        <label className={labelClass}>Descripción</label>
        <input className={inputClass} value={description ?? ""} onChange={(e) => setDescription(e.target.value)} placeholder="Opcional" />
      </div>

      <div>
        <label className={labelClass}>Producto</label>
        <select className={inputClass} value={product} onChange={(e) => setProduct(e.target.value)}>
          <option value="">Todos los productos</option>
          {LEAD_PRODUCTS.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <p className={`mt-1 text-[11px] ${tc.textSecondary}`}>El tablero mostrará solo los leads de este producto.</p>
      </div>

      <div>
        <label className={labelClass}>Categorías (en orden de columna)</label>
        <div className={`rounded-lg border ${tc.cardBorder}`}>
          {selected.length === 0 ? (
            <p className={`px-3 py-4 text-center text-xs ${tc.textSecondary}`}>Aún no has agregado categorías.</p>
          ) : (
            selected.map((cid, idx) => {
              const c = catById[cid];
              if (!c) return null;
              const cc = categoryClasses(c.color);
              return (
                <div key={cid} className={`flex items-center justify-between gap-2 border-b px-3 py-2 last:border-b-0 ${tc.cardBorder}`}>
                  <div className="flex items-center gap-2">
                    <span className={`h-2.5 w-2.5 rounded-full ${cc.dot}`} />
                    <span className={`text-sm ${tc.text}`}>{c.name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => move(idx, -1)} disabled={idx === 0} className={`rounded p-1 ${idx === 0 ? "text-gray-400/40" : `${tc.textSecondary} ${tc.buttonHover}`}`} title="Subir">
                      <i className="ri-arrow-up-line" />
                    </button>
                    <button onClick={() => move(idx, 1)} disabled={idx === selected.length - 1} className={`rounded p-1 ${idx === selected.length - 1 ? "text-gray-400/40" : `${tc.textSecondary} ${tc.buttonHover}`}`} title="Bajar">
                      <i className="ri-arrow-down-line" />
                    </button>
                    <button onClick={() => setSelected((prev) => prev.filter((x) => x !== cid))} className="rounded p-1 text-red-500 hover:bg-red-500/10" title="Quitar">
                      <i className="ri-close-line" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
        {available.length > 0 && (
          <select
            className={`${inputClass} mt-2`}
            value=""
            onChange={(e) => {
              if (e.target.value) setSelected((prev) => [...prev, e.target.value]);
            }}
          >
            <option value="">+ Agregar categoría…</option>
            {available.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className={`rounded-lg border px-4 py-2 text-sm ${tc.border} ${tc.textSecondary} ${tc.buttonHover}`}>
          Cancelar
        </button>
        <button onClick={submit} disabled={saving} className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary hover:bg-primary-400 disabled:opacity-60">
          {saving ? "Guardando..." : "Guardar"}
        </button>
      </div>
    </div>
  );
}
